import { isEmpty } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { useTrackAddedTagsToAssets, useTrackRemovedTagsFromAssets } from '@air/analytics';

import { AssetTaggingTasksPanel } from '~/components/AssetTaggingTasks/AssetTaggingTasksPanel';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useReloadApplication } from '~/providers/ReloadApplicationProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { removeTasksAction } from '~/store/tasks/actions';
import { makeAssetTaggingTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { ReadyState } from '~/types/sockets';
import { useUpdateAssetsInAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { getBoardIdFromPath } from '~/utils/PathUtils';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

export const PrivateAssetTaggingTasksPanel = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const tasksSelector = useMemo(
    () => makeAssetTaggingTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );
  const dispatch = useDispatch();
  const assetTaggingTasks = useAirSelector(tasksSelector);
  const { cancelTask } = useCancelTask();
  const { onAddPendingAction, onRemovePendingAction } = useReloadApplication();
  const { updateAssetsInAllViews } = useUpdateAssetsInAllViews();
  const { trackAddedTagsToAssets } = useTrackAddedTagsToAssets();
  const { trackRemovedTagsFromAssets } = useTrackRemovedTagsFromAssets();

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'AssetTagging',
    remoteType: 'AssetTagger',
    onComplete: async ({ task, localTask, enrichMetadata }) => {
      const { addedTags = [], removedTags = [], clipIds = [] } = task.data ?? {};

      // Update clips in memory across all views (gallery, table, kanban)
      if (clipIds.length > 0) {
        const boardId = getBoardIdFromPath(window.location.pathname);
        const removedTagIds = new Set(removedTags.map((t) => t.id));
        const tagsToAdd = addedTags.map((t) => ({ ...t, isDemo: false }));

        updateAssetsInAllViews({
          assetIdentifiers: clipIds,
          boardId: boardId || undefined,
          identifier: 'id',
          updateFn: (clip) => {
            const existingTags = clip.tags ?? [];
            const existingTagIds = new Set(existingTags.map((t) => t.id));
            const keptTags = existingTags.filter((t) => !removedTagIds.has(t.id));
            const newTags = tagsToAdd.filter((t) => !existingTagIds.has(t.id));
            const tags = [...keptTags, ...newTags];

            return { ...clip, tags };
          },
        });
      }

      enrichMetadata({ addedTags, removedTags, clipIds });

      if (localTask.metadata.trackLocation) {
        if (addedTags.length > 0) {
          trackAddedTagsToAssets({
            assets: clipIds,
            tags: addedTags,
            location: localTask.metadata.trackLocation,
            selectAll: true,
          });
        }
        if (removedTags.length > 0) {
          trackRemovedTagsFromAssets({
            assets: clipIds,
            tags: removedTags,
            location: localTask.metadata.trackLocation,
            selectAll: true,
          });
        }
      }
    },
  });

  const syncExecutedForReadyStateRef = useRef(false);

  /**
   * On mount, take the tasks from local storage and add them to Redux
   */
  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [loadFromStorage, currentWorkspace?.id]);

  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  /**
   * Reset sync flag when task subscriptions become not ready
   */
  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  /**
   * Sync tasks when WebSocket reconnects to catch any missed completion events.
   * Only sync when task subscriptions are confirmed ready to avoid race conditions.
   */
  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  /**
   * Also sync when task subscriptions become ready after reconnection.
   */
  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);

  useEffect(() => {
    if (!isEmpty(assetTaggingTasks)) {
      onAddPendingAction({ id: 'asset-tagging', description: 'Tagging assets' });
    } else {
      onRemovePendingAction('asset-tagging');
    }
  }, [onAddPendingAction, onRemovePendingAction, assetTaggingTasks]);

  /**
   * When this panel unmounts (user logs out most likely),
   * reset the state
   */
  useUnmount(() => {
    dispatch(
      removeTasksAction({
        taskIds: assetTaggingTasks.map(({ localTaskId }) => localTaskId),
      }),
    );
  });

  if (assetTaggingTasks.length < 1) {
    return null;
  }

  return <AssetTaggingTasksPanel assetTaggingTasks={assetTaggingTasks} onCancel={cancelTask} onClear={cancelTask} />;
});

PrivateAssetTaggingTasksPanel.displayName = 'PrivateAssetTaggingTasksPanel';
